import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Send, 
  Building2, 
  Users, 
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
  Megaphone,
  User
} from 'lucide-react';

const NotificationSender = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    recipientType: 'single', // 'single' or 'broadcast'
    companyId: '',
    title: '',
    message: '',
    senderName: '',
    type: 'INFO',
    priority: 'MEDIUM',
    actionUrl: '',
    actionLabel: '',
  });

  useEffect(() => {
    loadCompanies();
  }, [token]);

  const loadCompanies = async () => {
    try {
      if (!token) return;
      const items = await api.listOrganizations(token);
      setCompanies(items || []);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.senderName.trim()) {
        throw new Error('Mitarbeitername ist erforderlich');
      }

      const payload = {
        title: formData.title,
        message: formData.message,
        senderName: formData.senderName,
        type: formData.type,
        priority: formData.priority,
        actionUrl: formData.actionUrl || null,
        actionLabel: formData.actionLabel || null,
      };

      if (formData.recipientType === 'single') {
        if (!formData.companyId) {
          throw new Error('Bitte eine Organisation auswählen');
        }
        const result = await api.notificationSendToCompany({
          ...payload,
          companyId: formData.companyId,
        });
        setSuccess(`Nachricht erfolgreich an "${result.sentTo}" gesendet!`);
      } else {
        const result = await api.notificationBroadcast(payload);
        setSuccess(`Nachricht erfolgreich an ${result.count} Organisationen gesendet!`);
      }

      // Reset form
      setFormData({
        ...formData,
        title: '',
        message: '',
        senderName: '',
        actionUrl: '',
        actionLabel: '',
        companyId: formData.recipientType === 'single' ? formData.companyId : '',
      });
    } catch (err) {
      setError(err.message || 'Fehler beim Senden der Nachricht');
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'INFO', label: 'Info', icon: Info, color: 'text-blue-600' },
    { value: 'SUCCESS', label: 'Erfolg', icon: CheckCircle2, color: 'text-green-600' },
    { value: 'WARNING', label: 'Warnung', icon: AlertTriangle, color: 'text-yellow-600' },
    { value: 'ERROR', label: 'Fehler', icon: AlertCircle, color: 'text-red-600' },
    { value: 'ACTION_REQUIRED', label: 'Aktion erforderlich', icon: AlertCircle, color: 'text-orange-600' },
  ];

  const priorityOptions = [
    { value: 'LOW', label: 'Niedrig', color: 'bg-muted text-foreground' },
    { value: 'MEDIUM', label: 'Mittel', color: 'bg-blue-100 text-blue-700' },
    { value: 'HIGH', label: 'Hoch', color: 'bg-orange-100 text-orange-700' },
    { value: 'URGENT', label: 'Dringend', color: 'bg-red-100 text-red-700' },
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
          <Send className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Benachrichtigung senden</h2>
          <p className="text-sm text-muted-foreground">Sende Systemnachrichten an Organisationen</p>
        </div>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Recipient Type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Empfänger
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, recipientType: 'single', companyId: '' })}
              className={`p-4 border-2 rounded-lg transition-all flex items-center gap-3 ${
                formData.recipientType === 'single'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-border hover:border-border'
              }`}
            >
              <Building2 className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Einzelne Organisation</div>
                <div className="text-xs text-muted-foreground">An spezifische Firma senden</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, recipientType: 'broadcast' })}
              className={`p-4 border-2 rounded-lg transition-all flex items-center gap-3 ${
                formData.recipientType === 'broadcast'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-border hover:border-border'
              }`}
            >
              <Megaphone className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Alle Organisationen</div>
                <div className="text-xs text-muted-foreground">Broadcast an alle</div>
              </div>
            </button>
          </div>
        </div>

        {/* Company Selection (only for single) */}
        {formData.recipientType === 'single' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organisation auswählen <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required={formData.recipientType === 'single'}
            >
              <option value="">-- {t('merchant.actions.create.choose')} --</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name} ({company.status})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Sender Name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Dein Name (Absender) <span className="text-red-500">*</span>
            </div>
          </label>
          <input
            type="text"
            value={formData.senderName}
            onChange={(e) => setFormData({ ...formData, senderName: e.target.value })}
            placeholder="z.B. Max Mustermann"
            className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Wird den Empfängern als Absender angezeigt
          </p>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Titel <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="z.B. Wichtige Systemmeldung"
            className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Nachricht <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Detaillierte Nachricht an die Organisation(en)..."
            rows={4}
            className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            required
          />
        </div>

        {/* Type and Priority */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Typ
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Priorität
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action URL (optional) */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Aktions-URL (optional)
          </label>
          <input
            type="url"
            value={formData.actionUrl}
            onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
            placeholder="https://..."
            className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Action Label (optional) */}
        {formData.actionUrl && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Aktions-Button Text
            </label>
            <input
              type="text"
              value={formData.actionLabel}
              onChange={(e) => setFormData({ ...formData, actionLabel: e.target.value })}
              placeholder="z.B. Mehr erfahren"
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Submit Button */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Wird gesendet...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Nachricht senden
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default NotificationSender;
